/* ==========================================================================
   T.P. Mills Putter Finder - question flow (Launch Candidate)

   In scope:
     - Phase B    dataset loading, validation, warnings
     - Phase C-1  intro screen, 7-step question flow, answer capture
     - Phase C-2  rule-based scoring (assets/putter-finder-scoring.js)
     - Phase C-2B low-information handling ("Your Starting Direction")
     - Phase C-3  customer-facing result card, match strength label, compare
                  panel, and LIVE View Product navigation
     - Launch    Add to cart, optional email / handmade inquiry panels, and the
                  post-submit confirmation banner

   Two rules that shape this file:

   1. Step 1 (Line Confidence) has NO "Not Sure" option by owner decision, so
      a one-answer profile is reachable. It must not fake precision.
   2. The raw fit percentage is NEVER rendered to a customer. It orders the
      results and it goes to the dataLayer as raw_score_internal. What the
      customer reads is a strength label.

   Explicitly NOT implemented:
     - Shopify Flow automation, production GA4 configuration
     - automatic delivery of the result to the customer: the contact form
       reaches the STORE, and a human replies
   ========================================================================== */

const EXPECTED_COUNT = 15;
const REQUIRED_FIELDS = ['head_shape', 'tier', 'fit_notes'];
const BALANCE_EXEMPT = [
  't-p-mills-sgss-crocodile-handmade-putter',
  'sgss-flat-t-lucky-charm-light-gold-handmade-putter',
];

/* ---------------------------------------------------------------- questions */

const QUESTIONS = [
  {
    key: 'line_confidence',
    axis: 'Line Confidence',
    prompt: 'What costs you the most confidence on the green?',
    choices: [
      { value: 'short_putts', label: 'Short Putts', desc: 'Inside 6 feet feels uneasy.' },
      { value: 'distance_control', label: 'Distance Control', desc: 'Speed and roll-out vary too much.' },
      { value: 'start_line', label: 'Start Line', desc: 'The ball often starts offline.' },
      { value: 'strike_consistency', label: 'Strike Consistency', desc: 'Contact quality changes from putt to putt.' },
      { value: 'craft_rarity', label: 'Craft & Rarity', desc: 'I want something special.' },
    ],
  },
  {
    key: 'face_control',
    axis: 'Face Control',
    prompt: 'What does the face feel like when you miss?',
    choices: [
      { value: 'feels_open', label: 'Feels Open', desc: 'The ball tends to start right.' },
      { value: 'turns_over', label: 'Turns Over', desc: 'The ball tends to start left.' },
      { value: 'speed_changes', label: 'Speed Changes', desc: 'The ball finishes short or long.' },
      { value: 'contact_varies', label: 'Contact Varies', desc: 'Impact does not feel centered.' },
      { value: 'not_sure', label: 'Not Sure', desc: 'Let the finder guide me.' },
    ],
  },
  {
    key: 'stroke_shape',
    axis: 'Stroke Shape',
    prompt: 'How does your stroke naturally move?',
    choices: [
      { value: 'mostly_straight', label: 'Mostly Straight', desc: 'Minimal arc through the ball.' },
      { value: 'slight_arc', label: 'Slight Arc', desc: 'A small natural opening and closing motion.' },
      { value: 'stronger_arc', label: 'Stronger Arc', desc: 'A more visible inside-to-square-to-inside motion.' },
      { value: 'not_sure', label: 'Not Sure', desc: 'I do not know my stroke shape.' },
    ],
  },
  {
    key: 'visual_comfort',
    axis: 'Visual Comfort',
    prompt: 'What shape gives you confidence at address?',
    choices: [
      { value: 'classic_blade', label: 'Classic Blade', desc: 'Clean, traditional, compact.' },
      { value: 'wide_blade', label: 'Wide Blade', desc: 'Classic look with added stability.' },
      { value: 'compact_mallet', label: 'Compact Mallet', desc: 'Confidence without feeling oversized.' },
      { value: 'zero_torque', label: 'Zero Torque / ZT Style', desc: 'Modern stability and a square visual profile.' },
      { value: 'not_sure', label: 'Not Sure', desc: 'I want the recommendation to guide me.' },
    ],
  },
  {
    key: 'touch_feedback',
    axis: 'Touch Feedback',
    prompt: 'What do you want to feel through impact?',
    choices: [
      { value: 'soft_muted', label: 'Soft and Muted', desc: 'A smoother feel with less sharp feedback.' },
      { value: 'direct_feedback', label: 'Direct Feedback', desc: 'I want to feel exactly where I strike it.' },
      { value: 'stable_quiet', label: 'Stable and Quiet', desc: 'I want less twisting and less face movement.' },
      { value: 'speed_control', label: 'Speed Control', desc: 'I want better distance consistency.' },
      { value: 'not_sure', label: 'Not Sure', desc: 'I want the finder to guide me.' },
    ],
  },
  {
    key: 'putter_purpose',
    axis: 'Putter Purpose',
    prompt: 'What role should this putter play in your bag?',
    choices: [
      { value: 'everyday_gamer', label: 'Everyday Gamer', desc: 'A reliable putter for regular play.' },
      { value: 'stability_upgrade', label: 'Stability Upgrade', desc: 'A putter that feels easier to keep on line.' },
      { value: 'tour_inspired', label: 'Tour-Inspired Feel', desc: 'Premium feedback with a cleaner setup.' },
      { value: 'handmade_collectible', label: 'Handmade / Collectible', desc: 'A crafted putter with rarity and story.' },
      { value: 'not_sure_yet', label: 'Not Sure Yet', desc: 'I want the best starting point.' },
    ],
  },
  {
    key: 'length_preference',
    axis: 'Length Preference',
    prompt: 'What length do you usually prefer?',
    choices: [
      { value: 'len_33', label: '33 inches', desc: 'A slightly shorter setup.' },
      { value: 'len_34', label: '34 inches', desc: 'The most common starting point.' },
      { value: 'len_35', label: '35 inches', desc: 'A slightly longer setup.' },
      { value: 'not_sure', label: 'Not Sure', desc: 'Recommend based on available options.' },
    ],
  },
];

/* ------------------------------------------------------------------ helpers */

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

function track(name, payload) {
  if (!Array.isArray(window.dataLayer)) return;
  window.dataLayer.push(Object.assign({ event: name }, payload || {}));
}

/* -------------------------------------------------------------- diagnostics */

/* Phase C-3B: development diagnostics are OFF unless the URL carries
   finder_debug=1. Read once at load; nothing here reads or writes storage.

   The diagnostics code is deliberately still present and still runs its
   checks - console.error/console.log still fire so a developer sees problems
   in the console - only the on-page boxes are suppressed. */
function debugEnabled() {
  try {
    return new URLSearchParams(window.location.search).get('finder_debug') === '1';
  } catch (error) {
    return false;
  }
}

const PF_DEBUG = debugEnabled();

/* opts.customer === true marks a message that a real customer must still see
   (a hard failure that would otherwise leave a blank screen). Everything else
   is developer-only and is suppressed unless finder_debug=1. */
function renderStatus(host, { warn, title, lines, items, customer }) {
  if (!PF_DEBUG && !customer) return;
  const box = el('div', 'putter-finder__status' + (warn ? ' putter-finder__status--warn' : ''));
  box.appendChild(el('p', 'putter-finder__status-title', title));
  (lines || []).forEach((line) => box.appendChild(el('p', null, line)));
  if (items && items.length) {
    const ul = el('ul', 'putter-finder__status-list');
    items.forEach((item) => {
      const li = el('li', null);
      li.appendChild(el('span', 'putter-finder__code', item));
      ul.appendChild(li);
    });
    box.appendChild(ul);
  }
  host.appendChild(box);
}

function loadDataset(root, diag) {
  const node = root.querySelector('#putter-finder-data');

  if (!node) {
    console.error('[putter-finder] dataset script tag #putter-finder-data not found.');
    renderStatus(diag, {
      warn: true,
      title: 'Development warning',
      lines: ['Dataset element #putter-finder-data was not found in this section.'],
    });
    return null;
  }

  let payload;
  try {
    payload = JSON.parse(node.textContent);
  } catch (error) {
    console.error('[putter-finder] JSON.parse failed:', error);
    renderStatus(diag, {
      warn: true,
      title: 'Development warning',
      lines: ['JSON.parse failed on the dataset. See the browser console for details.'],
    });
    return null;
  }

  const products = Array.isArray(payload && payload.products) ? payload.products : [];
  const incomplete = Array.isArray(payload && payload.incomplete) ? payload.incomplete : [];
  const count = products.length;

  console.log('[putter-finder] dataset loaded:', count, 'products');

  renderStatus(diag, {
    warn: false,
    title: 'Scaffold status',
    lines: ['Dataset loaded: ' + count + ' products'],
  });

  if (count !== EXPECTED_COUNT) {
    const message =
      'Dataset count is ' + count + ', expected ' + EXPECTED_COUNT + ' (Phase A approved baseline).';
    console.warn('[putter-finder]', message);
    renderStatus(diag, {
      warn: true,
      title: 'Development warning: unexpected dataset size',
      lines: [message],
    });
  }

  const bad = [];
  products.forEach((product) => {
    const missing = REQUIRED_FIELDS.filter((field) => {
      const value = product[field];
      return value === null || value === undefined || value === '';
    });
    const balanceRequired = BALANCE_EXEMPT.indexOf(product.handle) === -1;
    if (balanceRequired && !product.balance) missing.push('balance');
    if (missing.length) bad.push(product.handle + ' - missing: ' + missing.join(', '));
  });

  if (bad.length) {
    console.warn('[putter-finder] products with missing required fields:', bad);
    renderStatus(diag, {
      warn: true,
      title: 'Development warning: required fields missing',
      lines: [bad.length + ' product(s) in the dataset are missing a required field:'],
      items: bad,
    });
  }

  if (incomplete.length) {
    const skipped = incomplete.map((entry) => entry.handle + ' - missing: ' + entry.missing.join(', '));
    console.warn('[putter-finder] products skipped for incomplete data:', skipped);
    renderStatus(diag, {
      warn: true,
      title: 'Development warning: skipped by the data snippet',
      lines: [incomplete.length + ' putter product(s) were excluded because required fit data is not filled in.'],
      items: skipped,
    });
  }

  return { products: products, count: count };
}

/* ------------------------------------------------------------------ the app */

var SCORING = (typeof window !== 'undefined' && window.PutterFinderScoring) || null;

function createFinder(root, app, dataset) {
  const copy = {
    eyebrow: root.dataset.pfEyebrow || 'T.P. Mills Putter Finder',
    heading: root.dataset.pfHeading || 'Find Your Mills Match',
    intro: root.dataset.pfIntro || 'A putter should fit your stroke, your eye, and your feel.',
    badge: root.dataset.pfBadge || 'No sign-up required. No email needed.',
    start: root.dataset.pfStart || 'Start Finder',
  };

  const state = { screen: 'intro', step: 0, answers: {}, lastResult: null, lastLabel: null };
  const live = root.querySelector('[data-pf-live]');

  function announce(message) {
    if (live) live.textContent = message;
  }

  function focusHeading() {
    const heading = app.querySelector('[data-pf-focus]');
    if (heading) heading.focus();
  }

  /* ----- intro ----- */
  function renderIntro() {
    app.textContent = '';
    app.appendChild(el('p', 'putter-finder__eyebrow', copy.eyebrow));

    const h = el('h2', 'putter-finder__heading', copy.heading);
    h.setAttribute('tabindex', '-1');
    h.setAttribute('data-pf-focus', '');
    app.appendChild(h);

    app.appendChild(el('p', 'putter-finder__intro', copy.intro));
    app.appendChild(el('p', 'putter-finder__badge', copy.badge));

    const btn = el('button', 'putter-finder__cta', copy.start);
    btn.type = 'button';
    btn.addEventListener('click', start);
    app.appendChild(btn);
  }

  function start() {
    state.screen = 'question';
    state.step = 0;
    state.answers = {};
    track('finder_started', { finder: 'putter_finder', total_steps: QUESTIONS.length });
    renderQuestion();
  }

  /* ----- question ----- */
  function renderQuestion() {
    const q = QUESTIONS[state.step];
    const stepNumber = state.step + 1;
    const total = QUESTIONS.length;

    app.textContent = '';

    /* progress: readable text, not colour alone */
    const progress = el('div', 'putter-finder__progress');
    progress.appendChild(
      el('p', 'putter-finder__progress-text', 'Step ' + stepNumber + ' of ' + total + ' \u00b7 ' + q.axis)
    );
    const bar = el('div', 'putter-finder__progress-bar');
    bar.setAttribute('aria-hidden', 'true');
    const fill = el('span', 'putter-finder__progress-fill');
    fill.style.width = Math.round((stepNumber / total) * 100) + '%';
    bar.appendChild(fill);
    progress.appendChild(bar);
    app.appendChild(progress);

    /* question group */
    const group = el('div', 'putter-finder__group');
    group.setAttribute('role', 'group');
    const headingId = 'pf-q-' + q.key;
    group.setAttribute('aria-labelledby', headingId);

    const prompt = el('h2', 'putter-finder__prompt', q.prompt);
    prompt.id = headingId;
    prompt.setAttribute('tabindex', '-1');
    prompt.setAttribute('data-pf-focus', '');
    group.appendChild(prompt);

    const list = el('div', 'putter-finder__choices');
    q.choices.forEach((choice) => {
      const btn = el('button', 'putter-finder__choice');
      btn.type = 'button';
      const selected = state.answers[q.key] && state.answers[q.key].value === choice.value;
      btn.setAttribute('aria-pressed', selected ? 'true' : 'false');
      if (selected) btn.classList.add('is-selected');
      btn.appendChild(el('span', 'putter-finder__choice-label', choice.label));
      btn.appendChild(el('span', 'putter-finder__choice-desc', choice.desc));
      btn.addEventListener('click', () => answer(q, choice));
      list.appendChild(btn);
    });
    group.appendChild(list);
    app.appendChild(group);

    app.appendChild(renderNav(state.step > 0));

    announce('Step ' + stepNumber + ' of ' + total + ', ' + q.axis + '. ' + q.prompt);
    focusHeading();
  }

  function answer(q, choice) {
    state.answers[q.key] = { axis: q.axis, value: choice.value, label: choice.label };
    track('question_answered', {
      finder: 'putter_finder',
      step: state.step + 1,
      axis: q.axis,
      question_key: q.key,
      answer_value: choice.value,
      answered_count: Object.keys(state.answers).length,
      meaningful_count: SCORING ? SCORING.lowInformation(state.answers).meaningfulCount : null,
    });

    if (state.step < QUESTIONS.length - 1) {
      state.step += 1;
      renderQuestion();
    } else {
      state.screen = 'result';
      renderResult();
    }
  }

  /* ----- navigation ----- */
  function renderNav(showBack) {
    const nav = el('div', 'putter-finder__nav');

    if (showBack) {
      const back = el('button', 'putter-finder__nav-btn', 'Back');
      back.type = 'button';
      back.addEventListener('click', () => {
        if (state.step > 0) {
          state.step -= 1;
          renderQuestion();
        }
      });
      nav.appendChild(back);
    }

    const restart = el('button', 'putter-finder__nav-btn', 'Restart');
    restart.type = 'button';
    restart.addEventListener('click', () => {
      state.screen = 'intro';
      state.step = 0;
      state.answers = {};
      announce('Finder restarted.');
      renderIntro();
      focusHeading();
    });
    nav.appendChild(restart);

    return nav;
  }

  /* ----- result view -----
     The customer-facing result surface lives in its own asset. Everything it
     needs is handed over explicitly; it reaches for nothing in this closure. */
  var RESULT = (typeof window !== 'undefined' && window.PutterFinderResult) || null;
  var resultView = RESULT ? RESULT.create({
    app: app,
    dataset: dataset,
    state: state,
    questions: QUESTIONS,
    scoring: SCORING,
    debug: PF_DEBUG,
    el: el,
    track: track,
    announce: announce,
    focusHeading: focusHeading,
    renderNav: renderNav,
    renderStatus: renderStatus,
  }) : null;

  function renderResult() {
    app.textContent = '';
    if (!resultView) {
      console.error('[putter-finder] result view not loaded (window.PutterFinderResult missing).');
      renderStatus(app, {
        warn: false,
        customer: true,
        title: 'We could not complete your match',
        lines: ['Your result could not be displayed.', 'You can browse the full T.P. Mills putter range instead.'],
      });
      var a = el('a', 'putter-finder__btn putter-finder__btn--primary', 'Browse all putters');
      a.href = '/collections/all-putters';
      var row = el('div', 'putter-finder__ctas');
      row.appendChild(a);
      app.appendChild(row);
      app.appendChild(renderNav(true));
      focusHeading();
      return;
    }
    resultView.render();
  }

  renderIntro();
}

/* --------------------------------------------------------------------- boot */

/* ------------------------------------------------ submission confirmation ---
   The email and inquiry panels post natively to Shopify's /contact endpoint,
   which is deliberate: a native post keeps Shopify's own spam protection in the
   loop and cannot silently swallow a lead the way a blocked AJAX call can.

   The cost is that the browser navigates, the finder resets, and without this
   banner the customer gets no acknowledgement at all - they type an email, the
   screen jumps, their result vanishes, and nothing is said. Confirmed live on
   2026-08-19. This closes that gap.

   Wording note: the contact form delivers the summary to the STORE, not to the
   customer. Until an automated send exists, the copy promises a reply rather
   than claiming the mail has already gone out. */
function contactConfirmation(root) {
  let posted = false;
  try {
    posted = new URLSearchParams(window.location.search).get('contact_posted') === 'true';
  } catch (error) {
    posted = false;
  }
  if (!posted) return;
  if (root.querySelector('.putter-finder__confirm')) return;

  const box = el('div', 'putter-finder__confirm');
  box.setAttribute('role', 'status');
  box.appendChild(el('p', 'putter-finder__confirm-title', 'Thank you \u2014 your message has been submitted.'));
  box.appendChild(el('p', null,
    'We will email your result summary shortly. Please check your inbox, and your spam folder if it does not arrive.'));
  root.insertBefore(box, root.firstChild);

  /* The finder sits below the page copy, so the banner would otherwise land
     off screen after the redirect. */
  if (typeof box.scrollIntoView === 'function') {
    try { box.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (error) { box.scrollIntoView(); }
  }
}

function boot(root) {
  const app = root.querySelector('[data-pf-app]');
  const diag = root.querySelector('[data-pf-diagnostics]') || root;
  if (!app) {
    console.error('[putter-finder] app container [data-pf-app] not found.');
    return;
  }

  contactConfirmation(root);

  if (!SCORING) {
    console.error('[putter-finder] scoring engine not loaded (window.PutterFinderScoring missing).');
    renderStatus(diag, {
      warn: true,
      title: 'Development warning',
      lines: ['assets/putter-finder-scoring.js did not load. The question flow will run but no result can be scored.'],
    });
  }

  const dataset = loadDataset(root, diag);
  createFinder(root, app, dataset);
  root.dataset.putterFinderReady = 'true';
}

function init() {
  document.querySelectorAll('[data-putter-finder]').forEach(boot);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
